import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import KitIcon from '../KitIcon';
import constants, { fontWeight } from '../../common/theme/constants';
import { KitFormInfoText } from './KitFormControls';

const { border } = constants;

const handleBottomBorderColor = (error, hover, disabled, readOnly, theme) => {
  if (error) {
    return theme.error;
  }
  if (disabled || readOnly) {
    return theme.input.normal.bottomBorder;
  }
  if (hover) {
    return theme.focus;
  }
  return theme.input.normal.bottomBorder;
};

const handleBackgroundColor = (theme, focused, disabled, readOnly) => {
  if (disabled || readOnly || focused) {
    return theme.input.focus.bg || 'inherit';
  }
  return theme.input.normal.bg || 'inherit';
};

export const KitInputStyles = css<{
  focused: boolean;
  error?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  withSearchIcon?: boolean;
}>`
  border: none;
  border-radius: 0;
  border-bottom: ${border.width}px solid
    ${({ theme, error, focused, disabled, readOnly }) =>
      handleBottomBorderColor(error, focused, disabled, readOnly, theme)};
  &:hover {
    border-bottom: ${border.width}px solid
      ${({ theme, error, disabled, readOnly }) =>
        handleBottomBorderColor(error, true, disabled, readOnly, theme)};
  }
  color: ${({ theme, disabled, readOnly }) =>
    disabled || readOnly ? theme.input.disabled.text : theme.input.normal.text};
  opacity: 1;
  font-weight: ${({ disabled, readOnly }) =>
    disabled || readOnly ? fontWeight.normal : fontWeight.bold};
  width: 100%;
  background-color: ${({ theme, focused, disabled, readOnly }) =>
    handleBackgroundColor(theme, focused, disabled, readOnly)};
  padding: 8px 8px 8px
    calc(8px + ${({ withSearchIcon }) => (withSearchIcon ? `${constants.iconSize.s}rem` : '0px')});
`;

const StyledInput = styled.input<{
  focused: boolean;
  error?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  withSearchIcon?: boolean;
}>`
  ${KitInputStyles}
`;

const RelativeContainer = styled.div`
  position: relative;
`;

const SearchIcon = styled(KitIcon)`
  position: absolute;
  left: 4px;
  top: 0;
  bottom: 0;
  margin-top: auto;
  margin-bottom: auto;
`;

type Ref = HTMLInputElement;
export interface KitInputProps extends React.ComponentPropsWithRef<'input'> {
  isError?: boolean;
  className?: string;
  infoMessage?: string;
  withSearchIcon?: boolean;
}

const KitInput = React.forwardRef<Ref, KitInputProps>((props, ref) => {
  const {
    isError,
    infoMessage = null,
    onBlur,
    required,
    type,
    withSearchIcon = false,
    ...otherProps
  } = props;
  const [focused, setFocused] = useState(false);

  const input = (
    <>
      <StyledInput
        focused={focused}
        error={isError}
        onFocus={() => {
          setFocused(true);
        }}
        onBlur={(e) => {
          setFocused(false);
          if (typeof onBlur === 'function') {
            onBlur(e);
          }
        }}
        ref={ref}
        aria-required={required}
        required={required}
        aria-invalid={isError}
        type={type}
        withSearchIcon={withSearchIcon}
        {...otherProps}
      />
      {infoMessage && <KitFormInfoText isError={isError} text={infoMessage} aria-live='polite' />}
    </>
  );

  if (withSearchIcon) {
    return (
      <RelativeContainer>
        <SearchIcon icon='magnify' size={`${constants.iconSize.s}rem`} />
        {input}
      </RelativeContainer>
    );
  }

  return input;
});

export default KitInput;
